import React from 'react';
import { Link } from 'react-router-dom';
import kitOrbitalDiagram from '../assets/kit-orbital-diagram.jpg';
import lenaDiagnosticParadigm from '../assets/lena-diagnostic-paradigm.jpg';

export default function Science() {
  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">Science & Validation</span>
          <h1 className="hero-headline" style={{ marginBottom: 24 }}>The evidence behind the platform.</h1>
          <p className="body-lg" style={{ maxWidth: 720 }}>
            Every claim Alercell makes is grounded in data. This page presents the biological rationale, the theoretical framework governing cellular transformation, the ALER-5 cohort validation dataset, the AI architecture, and the scientific record underlying the LENA platform.
          </p>
          <div style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
            {['The Biology', 'Our Framework', 'Validation Data', 'AI & ML', 'Publications'].map(s => (
              <a key={s} href={`#${s.toLowerCase().replace(/ /g, '-').replace('&', '').replace('--', '-')}`}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em', textDecoration: 'none', padding: '6px 14px', border: '1px solid var(--accent-cyan-border)', borderRadius: 20 }}>
                {s}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Biology */}
      <section id="the-biology" className="section section-surface">
        <div className="container">
          <span className="eyebrow">The Biology</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Why epigenetics sees leukemia first.</h2>
          <div className="cyan-line" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }} className="bio-grid">
            {[
              {
                title: 'What DNA Methylation Is',
                text: 'DNA methylation is a chemical modification — the addition of a methyl group (CH₃) to the cytosine base at CpG dinucleotides — that regulates gene expression without altering the DNA sequence itself. DNMT and TET enzyme families write and erase these marks. In healthy cells, methylation patterns are tightly regulated and cell-type specific, governing which genes are active and which are silenced.',
              },
              {
                title: 'Methylation Drift in Pre-Leukemic Cells',
                text: 'In clonal hematopoiesis of indeterminate potential (CHIP) — the earliest detectable precursor to AML — hematopoietic stem cells begin acquiring aberrant methylation patterns at regulatory regions governing HOX clusters, CDKN2B, and RUNX1 gene loci. These changes are detectable in peripheral blood via standard methylation array profiling, making blood an effective liquid biopsy matrix for pre-leukemic surveillance.',
              },
              {
                title: 'Why Methylation Precedes Mutation',
                text: 'The temporal ordering of DNMT3A, TET2, and ASXL1 mutations relative to epigenetic reprogramming has been established in multiple longitudinal cohort studies. Epigenetic reprogramming precedes driver mutation acquisition. This means methylation-based tests have a structural biological advantage over next-generation sequencing approaches: they read an earlier chapter of the leukemogenic story.',
              },
              {
                title: 'Why Genetics-Only Liquid Biopsy Misses Early Disease',
                text: 'Conventional ctDNA and NGS-based liquid biopsy approaches detect circulating tumor DNA based on variant allele frequency (VAF). In early CHIP, clonal expansion is below the limit of detection — VAF may be <0.1%. Furthermore, in aging populations, CHIP clones carrying identical mutations are present in non-malignant cells, creating specificity challenges. Methylation patterns provide signal orthogonal to mutation status, enabling detection before mutation is even the right question.',
              },
            ].map((b, i) => (
              <div key={i} className="bio-block">
                <div className="bio-block-title">{b.title}</div>
                <div className="bio-block-text">{b.text}</div>
              </div>
            ))}
          </div>
          <style>{`@media(max-width:768px){.bio-grid{grid-template-columns:1fr!important;}}`}</style>
        </div>
      </section>

      {/* KIT Framework */}
      <section id="our-framework" className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center', marginBottom: 40 }} className="bio-grid">
            <div>
              <span className="eyebrow">A Different Way to Think About Cancer</span>
              <h2 className="section-headline" style={{ maxWidth: 800, marginBottom: 20 }}>
                Oncology asks "does this patient have cancer?" We ask a more useful question: where is this patient going — and how fast?
              </h2>
            </div>
            <div style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', background: '#ffffff', padding: 16 }}>
              <img
                src={lenaDiagnosticParadigm}
                alt="From Snapshot to Trajectory — Traditional diagnostics vs Kinetic Information Theory"
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
              />
            </div>
          </div>
          <div className="cyan-line" />
          <div style={{ maxWidth: 720, marginBottom: 40 }}>
            <p className="body-md" style={{ marginBottom: 20 }}>
              Every diagnostic test in oncology today answers the same question: is cancer present right now? That is a reasonable question — but by the time the answer is "yes," the disease has typically been developing for years. Alercell was built on a different premise: that the cellular changes leading to leukemia follow a predictable path, and that path is readable from a blood sample years before any symptom appears.
            </p>
            <p className="body-md" style={{ marginBottom: 20 }}>
              We call this approach <strong style={{ color: 'var(--text-primary)' }}>Kinetic Information Theory (KIT)</strong>. Instead of taking a snapshot of a patient's biology and asking "what is present?" we measure how their biology is <em>moving</em> — its direction, its speed, and how far it is from the point of no return.
            </p>
            <p className="body-md">
              The distinction matters clinically: two patients can have identical test results today but be on completely different trajectories — one stable and one accelerating toward disease. Static tests cannot distinguish them. KIT is designed to.
            </p>
          </div>

          <div className="stat-grid">
            {[
              { title: 'Trajectory Direction', body: 'Is the patient\'s methylation profile moving toward or away from leukemic transformation? KIT measures the vector, not just the position.' },
              { title: 'Rate of Change', body: 'How fast is biological drift occurring? Acceleration is a key clinical signal that static tests miss entirely.' },
              { title: 'Distance to Threshold', body: 'How far is the patient from the transformation threshold? KIT quantifies the clinical buffer — the time available for intervention.' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderLeft: '3px solid var(--accent-cyan)', borderRadius: '0 var(--radius) var(--radius) 0', padding: 28 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{item.title}</h3>
                <p className="body-md">{item.body}</p>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 40, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32 }}>
            <span className="eyebrow">KIT in Practice</span>
            <p className="body-md" style={{ marginBottom: 12 }}>
              In an internal retrospective analysis (not yet peer-reviewed) of 190,000 patient records, KIT signatures were detectable a mean of 3.2 years before conventional diagnosis — with 94.7% identification sensitivity for historical transformation patterns. The maximum observed detection window was over 10 years before diagnosis.
            </p>
            <p className="source-cite">Source: Scheer F. Cancer as Trajectory: A Kinetic Information Theory of Epigenetic Transformation. Alercell, Inc. / bioRxiv preprint, 2025. [Manuscript in preparation — not yet peer-reviewed.]</p>
          </div>
        </div>
      </section>

      {/* Orbital Framework */}
      <section className="section section-surface">
        <div className="container">
          <span className="eyebrow">Predicting the Future, Not Just Measuring the Present</span>
          <h2 className="section-headline" style={{ maxWidth: 800, marginBottom: 20 }}>
            Cancer follows a predictable trajectory. We have found the equation that describes it.
          </h2>
          <div className="cyan-line" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'start' }} className="bio-grid">
            <div>
              <p className="body-md" style={{ marginBottom: 20 }}>
                A satellite orbiting Earth doesn't follow a random path. It follows precise laws of physics — laws that let scientists predict exactly where that satellite will be months or years from now. Alercell's research suggests that pre-leukemic cells follow analogous laws: their methylation trajectories are not random noise, but predictable paths through a biological energy landscape that obeys a conservation principle.
              </p>
              <p className="body-md" style={{ marginBottom: 20 }}>
                Analyzing the full 199,800-patient ALER-5 cohort, Alercell's scientific team established that a patient's own earlier blood draw is the most informative reference for a later one — a <strong style={{ color: 'var(--text-primary)' }}>two-draw displacement</strong> that cancels site and platform offsets and isolates the direction and rate of change relative to that patient's baseline.
              </p>
              <p className="body-md">
                The practical implication: two blood measurements, taken at different points in time, are enough to characterize the direction and magnitude of a patient's trajectory against a pre-specified healthy null. This moves the LENA platform from a diagnostic snapshot toward a trajectory model; whether that trajectory predicts clinical disease is the question our prospective studies are designed to answer.
              </p>
            </div>
            <div>
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32, marginBottom: 24 }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>What This Changes Clinically</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr><th style={{ textAlign: 'left', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', paddingBottom: 12, borderBottom: '1px solid var(--border-cyan)' }}>Question</th><th style={{ textAlign: 'left', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', paddingBottom: 12, borderBottom: '1px solid var(--border-cyan)', paddingLeft: 16 }}>Standard Tests</th><th style={{ textAlign: 'left', color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', paddingBottom: 12, borderBottom: '1px solid var(--border-cyan)', paddingLeft: 16 }}>KIT / LENA</th></tr></thead>
                  <tbody>
                    {[
                      ['Is disease present?', '✓', '✓'],
                      ['How far from threshold?', '✗', '✓'],
                      ['Rate of progression?', '✗', '✓'],
                      ['Time to intervene?', '✗', '✓'],
                    ].map(([q, s, l], i) => (
                      <tr key={i}><td style={{ padding: '10px 0', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{q}</td><td style={{ paddingLeft: 16, borderBottom: '1px solid var(--border)', color: s === '✓' ? '#00C864' : 'var(--text-muted)' }}>{s}</td><td style={{ paddingLeft: 16, borderBottom: '1px solid var(--border)', color: l === '✓' ? '#00C864' : 'var(--text-muted)' }}>{l}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ background: 'rgba(255,180,0,0.08)', border: '1px solid rgba(255,180,0,0.25)', borderRadius: 'var(--radius)', padding: 20 }}>
                <p style={{ fontSize: 12, color: 'rgba(255,200,50,0.9)', lineHeight: 1.5 }}>
                  <strong>Scientific Status:</strong> The conservation finding is based on retrospective analysis of the ALER-5 cohort. A prospective longitudinal study is currently being designed to confirm the conservation principle going forward. Manuscript in preparation (Scheer, 2026).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ALER-5 Validation */}
      <section id="validation" className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">ALER-5 Cohort Validation</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>The largest methylation-survival dataset in hematologic oncology.</h2>
          <div className="cyan-line" />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginBottom: 48 }} className="bio-grid">
            <div>
              <p className="body-md" style={{ marginBottom: 16 }}>
                The ALER-5 validation cohort is the evidentiary foundation of the LENA platform. It comprises 538,090 longitudinal clinical observations from 199,800 de-identified patient electronic medical records, assembled across 5 independent clinical cohorts, with methylation reference data profiled on 3 Illumina array platforms.
              </p>
              <p className="body-md">
                The dataset includes SEER-linked validation in 264,000 patients and NCI Cox proportional hazards modeling in 11,663 patients with confirmed survival outcomes.
              </p>
            </div>
            <div>
              <table className="data-table">
                <tbody>
                  {[
                    ['Total observations', '538,090 longitudinal clinical data points'],
                    ['Patient records', '199,800 de-identified EMRs'],
                    ['Independent cohorts', '5 clinical cohorts'],
                    ['Array platforms', 'Illumina 450K, EPIC v1.0, EPIC v2.0'],
                    ['SEER validation', '264,000 patients (linked)'],
                    ['Survival modeling', '11,663 patients (NCI Cox PH)'],
                    ['Max detection window', 'Up to 10 years before diagnosis (retrospective, internal)'],
                    ['Median detection window', '3.49 years (41.9 months) (retrospective, internal)'],
                    ['CpG sites analyzed', 'Proprietary diagnostic panel (IP protected)'],
                  ].map(([k, v], i) => (
                    <tr key={i}><td className="highlight">{k}</td><td>{v}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Performance Metrics */}
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Primary Performance Metrics — ALER-5 Cohort</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20, fontFamily: 'var(--font-mono)' }}>48-site CpG panel, cross-sectional analysis. 95% CI pending publication.</p>
          <table className="data-table" style={{ marginBottom: 48 }}>
            <thead><tr><th>Metric</th><th>Value</th><th>Clinical Interpretation</th></tr></thead>
            <tbody>
              {[
                ['AUC (ROC)', '0.967', 'Retrospective discrimination between pre-leukemic and normal trajectories (internal analysis, not yet peer-reviewed)'],
                ['Sensitivity', '93–97%*', 'Of patients on pre-leukemic trajectories, LENA correctly identifies them'],
                ['Specificity', '87–93%*', 'Of healthy controls, LENA correctly rules out pre-leukemic transformation'],
                ['Detection lead time (median)', '41 months', '3.49 years before conventional diagnosis on average, in retrospective analysis'],
                ['Detection lead time (max)', '>10 years', 'Maximum observed pre-symptomatic window in ALER-5 cohort'],
                ['KIT sensitivity (190K records)', '94.7%', 'Full kinetic framework applied to retrospective cohort'],
              ].map(([m, v, c], i) => (
                <tr key={i}><td className="highlight">{m}</td><td style={{ color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>{v}</td><td>{c}</td></tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 48 }}>
            *Performance data range reflects different analytical endpoints. Confirmed figures with 95% CI will be published in the forthcoming manuscript.
          </p>

          {/* Standard of Care Comparison */}
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>Comparison to Current Standard of Care</h3>
          <table className="data-table">
            <thead><tr><th>Modality</th><th>Detection Timing</th><th>Mechanism</th><th>Limitation</th></tr></thead>
            <tbody>
              {[
                ['LENA Platform (Alercell)', 'Up to 10 years pre-diagnosis', 'DNA methylation drift (epigenetic)', '—'],
                ['Conventional cytogenetics', 'At or after diagnosis', 'Chromosomal abnormalities', 'Requires disease to be present'],
                ['NGS liquid biopsy (ctDNA)', 'Late CHIP / early disease', 'Somatic mutation (VAF)', 'Below LOD in early CHIP'],
                ['CBC / flow cytometry', 'At or after clinical symptoms', 'Blast count, morphology', 'No pre-symptomatic sensitivity'],
                ['Bone marrow biopsy', 'At diagnosis, staging', 'Histology, cytology', 'Invasive; no screening utility'],
              ].map(([m, t, mech, lim], i) => (
                <tr key={i}>
                  <td className="highlight" style={{ color: i === 0 ? 'var(--accent-cyan)' : 'var(--text-primary)' }}>{m}</td>
                  <td style={{ color: i === 0 ? 'var(--accent-cyan)' : 'var(--text-secondary)' }}>{t}</td>
                  <td>{mech}</td><td>{lim || <span style={{ color: 'var(--accent-cyan)' }}>First-in-class</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* AI Architecture */}
      <section id="ai-ml" className="section section-surface">
        <div className="container">
          <span className="eyebrow">The Computational Architecture</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>A neuro-symbolic AI engine purpose-built for high-dimensional epigenetic data.</h2>
          <div className="cyan-line" />
          <div className="stat-grid">
            {[
              { title: 'Module 1: LENA Code® Normalizer', body: 'Cross-platform batch correction using ComBat-seq harmonization adapted for methylation beta-values. Removes technical variance across 450K, EPIC v1.0, and EPIC v2.0 arrays.' },
              { title: 'Module 2: Feature Selection Engine', body: 'Elastic Net regularization with LASSO penalty for CpG site selection. Identifies the minimal discriminative panel from 850,000+ candidate sites, balancing sensitivity and interpretability.' },
              { title: 'Module 3: KIT Trajectory Calculator', body: 'Computes epigenetic velocity vectors and acceleration scalars from serial methylation profiles. Implements the two-draw displacement statistic at the core of Kinetic Information Theory.' },
              { title: 'Module 4: LENA Score™ Generator', body: 'Gradient-boosted ensemble classifier trained on the ALER-5 cohort (5-fold cross-validation). Outputs a continuous risk score (0–100) calibrated to clinical pre-leukemic probability.' },
              { title: 'Module 5: LENA-Rx™ Integration', body: 'Rule-based symbolic reasoning layer mapping LENA Score ranges to treatment pathways, augmented by real-time PubMed and ClinicalTrials.gov API calls for variant-specific guidance.' },
            ].map((m, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent-cyan)', display: 'block', marginBottom: 8 }}>MODULE {i + 1}</span>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{m.title}</h3>
                <p className="body-md">{m.body}</p>
              </div>
            ))}
          </div>

          {/* Agentic LENA statement */}
          <div style={{ marginTop: 40, background: 'var(--bg-card)', border: '1px solid var(--accent-cyan-border)', borderRadius: 'var(--radius)', padding: 32 }}>
            <p className="body-md" style={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
              LENA is an agentic diagnostic application deployed on a cloud-based SaaS platform. Unlike traditional diagnostic software that requires physician interpretation and manual integration at each decision point, LENA operates as an autonomous agent embedded within clinical workflows — receiving orders, executing proprietary risk assessment algorithms, delivering evidence-backed predictions, and integrating results directly into electronic health records and clinical decision pathways.
            </p>
          </div>

          {/* KIT Orbital Diagram */}
          <div style={{ marginTop: 48 }}>
            <span className="eyebrow">Kinetic Information Theory — Trajectory Framework Visualization</span>
            <div style={{ marginTop: 20, borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)', background: '#ffffff', padding: 24 }}>
              <img
                src={kitOrbitalDiagram}
                alt="KIT Orbital Framework — Satellite orbit analogy for epigenetic leukemic trajectory"
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
              />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 12, textAlign: 'center' }}>
              Left: Satellite in elliptical orbit (gravitational conservation law E = ½v² − GM/r). Right: Pre-leukemic patient trajectory in methylation space (E_epi = ½k² − Γ/d(M)). Both systems conserve energy along trajectory — enabling prediction from two time points.
            </p>
          </div>
        </div>
      </section>

      {/* Publications */}
      <section id="publications" className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">Publications & White Papers</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>The scientific record.</h2>
          <div className="cyan-line" />

          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 20 }}>Alercell Manuscripts</h3>
          {[
            { title: 'Cancer as Trajectory: A Kinetic Information Theory of Epigenetic Transformation', authors: 'Scheer F. et al.', status: 'Manuscript in preparation for peer review.' },
            { title: 'Epigenetic Orbital Conservation in Pre-Leukemic Hematopoietic Trajectories', authors: 'Scheer F. et al.', status: 'Manuscript in preparation, 2026.' },
          ].map((pub, i) => (
            <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24, marginBottom: 16 }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>{pub.title}</p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>{pub.authors}</p>
              <span className="badge badge-progress">{pub.status}</span>
            </div>
          ))}

          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: '40px 0 20px' }}>White Papers</h3>
          <div className="platform-grid">
            {[
              { title: 'LENA Platform Technical Overview', access: 'Available on Request (NDA)' },
              { title: 'ALER-5 Cohort Validation Data Summary', access: 'Available on Request (NDA)' },
              { title: 'Kinetic Information Theory: Plain Language Guide', access: 'Available on Request (NDA)' },
              { title: 'Regulatory Pathway Analysis: LDT to De Novo', access: 'Available on Request (NDA)' },
              { title: 'Deep Leukemic Cloud Architecture', access: 'Available on Request (NDA)' },
            ].map((wp, i) => (
              <div key={i} className="card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>{wp.title}</h4>
                <div>
                  <span className={`badge badge-progress`} style={{ marginBottom: 12, display: 'inline-block' }}>{wp.access}</span>
                  <Link to="/contact?type=whitepaper" className="btn btn-text" style={{ display: 'block', fontSize: 13 }}>Request</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-surface">
        <div className="container cta-banner">
          <h2 className="section-headline">Want the full data package?</h2>
          <div className="cta-buttons">
            <Link to="/contact?type=whitepaper" className="btn btn-primary">Request White Paper Package</Link>
            <Link to="/platform" className="btn btn-outline">View the Platform</Link>
            <Link to="/investors" className="btn btn-text">Investor Relations</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
